import { ClientMergeFieldSourcesInput, ClientMergeSideFragment } from '~/api/graphql';

import { formatAddress } from '~/components/clients/clients-columns';

export type MergeClient = ClientMergeSideFragment;

export type MergeFieldKey = keyof ClientMergeFieldSourcesInput;

const MERGE_FIELDS: { key: MergeFieldKey; label: string }[] = [
  { key: 'email', label: 'Email' },
  { key: 'phone', label: 'Phone' },
  { key: 'address', label: 'Address' },
  { key: 'agencyUserOwner', label: 'Owner' }
];

/**
 * One field as the comparison sees it. `key` is what decides whether two
 * records agree, and is the owner's id rather than their name: two users can
 * share a name, and the merge would otherwise call an owner change a no-op.
 */
type FieldValue = { text: string; key: string } | null;

const fieldValue = (client: MergeClient, field: MergeFieldKey): FieldValue => {
  switch (field) {
    case 'email':
      return client.email ? { text: client.email, key: client.email } : null;
    case 'phone':
      return client.phone ? { text: client.phone, key: client.phone } : null;
    case 'address': {
      // Present whenever the server has an address at all, since that is what
      // stops it filling the survivor's from another client, even one whose
      // parts are all blank.
      if (client.address == null) return null;
      const text = formatAddress(client.address);
      return { text: text ?? 'Blank address', key: text ?? '' };
    }
    case 'agencyUserOwner':
      return client.agencyUserOwner
        ? { text: client.agencyUserOwner.name, key: client.agencyUserOwner.id }
        : null;
  }
};

/** Which client each field is taken from, by id. */
export type FieldPicks = Partial<Record<MergeFieldKey, string>>;

/** One distinct value of a field, and every client that holds it. */
export type FieldOption = { value: NonNullable<FieldValue>; clientIds: string[] };

/** One field of the merge, decided. */
export type FieldRow = {
  key: MergeFieldKey;
  label: string;
  /** Every client's value, in merge order. */
  values: { client: MergeClient; value: FieldValue }[];
  options: FieldOption[];
  /** More than one distinct value, so the person has to choose. */
  conflict: boolean;
  /** The client the merged record takes the value from. Null when none has one. */
  sourceId: string | null;
  merged: FieldValue;
};

/** The survivor first, then the rest in the order they will be merged. */
export const mergeOrder = (clients: MergeClient[], survivorId: string): MergeClient[] => [
  ...clients.filter((c) => c.id === survivorId),
  ...clients.filter((c) => c.id !== survivorId)
];

/**
 * Decides every field once, by the same rule the server applies when nothing is
 * chosen: the first client in merge order with a value supplies it, which is the
 * survivor wherever the survivor has one. A pick overrides that on a conflict.
 */
export const compareFields = (ordered: MergeClient[], picks: FieldPicks): FieldRow[] =>
  MERGE_FIELDS.map(({ key, label }) => {
    const values = ordered.map((client) => ({ client, value: fieldValue(client, key) }));
    const options: FieldOption[] = [];
    for (const { client, value } of values) {
      if (!value) continue;
      const existing = options.find((option) => option.value.key === value.key);
      if (existing) {
        existing.clientIds.push(client.id);
      } else {
        options.push({ value, clientIds: [client.id] });
      }
    }
    const conflict = options.length > 1;
    const picked = conflict
      ? values.find((entry) => entry.value && entry.client.id === picks[key])
      : undefined;
    const source = picked ?? values.find((entry) => entry.value);
    return {
      key,
      label,
      values,
      options,
      conflict,
      sourceId: source?.client.id ?? null,
      merged: source?.value ?? null
    };
  });

/**
 * Only the picks a person made on conflicting fields are sent. The rest follow
 * the server's default rule, which is the one the preview applied, and a merge
 * with no picks is not stamped as asserted by the person.
 */
export const fieldsInputOf = (rows: FieldRow[], picks: FieldPicks): ClientMergeFieldSourcesInput =>
  Object.fromEntries(
    rows
      .filter((row) => row.conflict && picks[row.key] && row.sourceId === picks[row.key])
      .map((row) => [row.key, picks[row.key]])
  );

/** A client's values the merged record does not keep. */
export const droppedValues = (client: MergeClient, rows: FieldRow[]) =>
  rows.flatMap((row) => {
    const value = row.values.find((entry) => entry.client.id === client.id)?.value;
    return value && value.key !== row.merged?.key ? [{ label: row.label, text: value.text }] : [];
  });
